import { useEffect, useState, type CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assetsApi, type AvailabilityGroup, type TypeAvailability } from "../api/assets";
import { assetTypesApi } from "../api/catalog";
import { layoutsApi } from "../api/layouts";
import type { ApiError } from "../api/client";
import { useToast } from "../components/Toast";
import { useAuth } from "../auth/AuthContext";
import { PermLink } from "../components/PermLink";
import { AssetTypeCombobox, type AssetTypeRef } from "../components/AssetTypeCombobox";
import { ConditionChip, UsageChip, conditionLabel, usageLabel } from "../components/ConditionChip";
import { AssetAttributesTable } from "../components/AssetAttributesTable";
import { AdvancedAssetSearch } from "../components/AdvancedAssetSearch";
import { ReplacementBadge } from "../components/AssetSummaryCard";
import { formatDate } from "../lib/dates";
import { pathLabel, shortPathLabel } from "../lib/paths";
import Ic from "../components/icons.jsx";

/**
 * `/pretraga` — "Koji deo tražiš?": pick a type, get every asset of that type
 * grouped by location × condition × usage, operational stock first
 * (contract §5 `GET /asset-types/{id}/availability`, §7).
 */
export default function SearchPartPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const typeId = params.get("typeId");
  // "dostupnost" (default) = where the pieces are; "atributi" = every asset of the type with all attribute
  // values; "napredna" = criteria over every column and attribute, no type required (sprint 5i).
  const viewParam = params.get("view");
  const view: PartSearchView = viewParam === "atributi" || viewParam === "napredna" ? viewParam : "dostupnost";
  const [type, setType] = useState<AssetTypeRef | null>(null);
  const [data, setData] = useState<TypeAvailability | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Deep link `?typeId=` → resolve the label for the combobox.
  useEffect(() => {
    if (!typeId) { setType(null); setData(null); return; }
    if (type?.id === typeId) return;
    let cancelled = false;
    assetTypesApi.get(typeId)
      .then((x) => { if (!cancelled) setType({ id: x.id, code: x.code, name: x.name }); })
      .catch(() => { if (!cancelled) setType({ id: typeId, code: "", name: "" }); });
    return () => { cancelled = true; };
    // `type` is only read to skip a redundant fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId]);

  useEffect(() => {
    if (!typeId || view !== "dostupnost") return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    assetsApi.availability(typeId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err: ApiError) => { if (!cancelled) { setError(err.message); setData(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [typeId, view]);

  function setView(next: PartSearchView) {
    const sp = new URLSearchParams(params);
    if (next === "dostupnost") sp.delete("view"); else sp.set("view", next);
    // The criteria belong to the advanced view only.
    if (next !== "napredna") sp.delete("f");
    setParams(sp, { replace: true });
  }

  function pick(next: AssetTypeRef | null) {
    setType(next);
    const sp = new URLSearchParams(params);
    if (next) sp.set("typeId", next.id); else sp.delete("typeId");
    setParams(sp, { replace: true });
  }

  const inStockGroup = data?.groups.find((g) => g.condition === "ISPRAVAN" && g.usage === "NA_LAGERU");

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("partSearch.title", "Pretraga dela")}</h1>
          <div className="sub">{t("partSearch.subtitle", "Koji deo tražiš? Izaberi tip — prvo ispravni na lageru, pa demontirani i servis.")}</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-b">
          <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
            {view === "napredna" ? (
              <div className="muted" style={{ flex: "1 1 320px", fontSize: 13 }}>
                {t("advSearch.intro", "Pretraga po više kriterijuma: tip, stanje, mesto i bilo koji atribut — na primer svi ispravni elektromotori od 100 do 150 kW.")}
              </div>
            ) : (
              <label className="field" style={{ flex: "1 1 320px", maxWidth: 560 }}>
                <span>{t("partSearch.type", "Tip dela")}</span>
                <AssetTypeCombobox value={type} onChange={pick} placeholder={t("partSearch.typePlaceholder", "Šifra ili naziv tipa, npr. EM-110KW…")} />
              </label>
            )}
            <div className="seg" role="group" aria-label={t("partSearch.view", "Prikaz")}>
              <button type="button" className={view === "dostupnost" ? "is-on" : ""} aria-pressed={view === "dostupnost"} onClick={() => setView("dostupnost")}>
                {t("partSearch.view.availability", "Dostupnost")}
              </button>
              <button type="button" className={view === "atributi" ? "is-on" : ""} aria-pressed={view === "atributi"} onClick={() => setView("atributi")}>
                {t("partSearch.view.attributes", "Svi atributi")}
              </button>
              <button type="button" className={view === "napredna" ? "is-on" : ""} aria-pressed={view === "napredna"} onClick={() => setView("napredna")}>
                {t("partSearch.view.advanced", "Napredna pretraga")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      {view === "napredna" ? (
        <AdvancedAssetSearch />
      ) : !typeId ? (
        <div className="card"><div className="empty">{t("partSearch.pickType", "Izaberite tip dela da vidite gde ima komada.")}</div></div>
      ) : view === "atributi" ? (
        <AssetAttributesTable typeId={typeId} typeLabel={type && (type.code || type.name) ? `${type.code} ${type.name}`.trim() : undefined} />
      ) : loading && !data ? (
        <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>
      ) : data ? (
        <>
          <div className="section-h" style={{ marginBottom: 12 }}>
            <h3 style={{ fontSize: 16 }}>
              <span className="mono" style={{ color: "var(--ink-3)", marginRight: 6 }}>{data.typeCode}</span>{data.typeName}
            </h3>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link to={`/tipovi/${data.typeId}`} className="btn sm" style={{ textDecoration: "none" }}>{t("partSearch.openType", "Otvori tip")} <Ic.arrowRight /></Link>
              <Link to={`/asseti?typeId=${data.typeId}`} className="btn sm" style={{ textDecoration: "none" }}>{t("partSearch.allAssets", "Svi asseti tipa")}</Link>
            </div>
          </div>

          <div className="kpi-grid avail-kpis">
            <Kpi label={t("partSearch.kpi.total", "Ukupno")} value={data.total} />
            <Kpi label={t("partSearch.kpi.inStock", "Ispravnih na lageru")} value={data.inStock} tone={data.inStock > 0 ? "ok" : "err"} />
            <Kpi label={t("partSearch.kpi.mounted", "Montirano")} value={data.mounted} />
            <Kpi label={t("partSearch.kpi.service", "Na servisu")} value={data.byCondition.NA_SERVISU ?? 0} tone="warn" />
            <Kpi label={t("partSearch.kpi.broken", "Neispravno")} value={data.byCondition.NEISPRAVAN ?? 0} tone="err" />
          </div>

          {!inStockGroup && data.total > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="empty">{t("partSearch.noneInStock", "Nema ispravnih na lageru — proveri demontirane i servis ispod.")}</div>
            </div>
          )}
          {data.total === 0 && (
            <div className="card"><div className="empty">{t("partSearch.noneAtAll", "Nema nijednog asseta ovog tipa u registru.")}</div></div>
          )}

          {data.groups.map((g, i) => (
            <GroupCard key={groupKey(g, i)} group={g} primary={g.condition === "ISPRAVAN" && g.usage === "NA_LAGERU"} />
          ))}
        </>
      ) : null}
    </div>
  );
}

type PartSearchView = "dostupnost" | "atributi" | "napredna";

function groupKey(g: AvailabilityGroup, i: number): string {
  return `${g.location?.id ?? "none"}|${g.condition}|${g.usage}|${i}`;
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: "ok" | "warn" | "err" }) {
  return (
    <div className="kpi">
      <div className="label">{label}</div>
      <div className="val" style={tone ? { color: `var(--${tone})` } : undefined}>{value}</div>
    </div>
  );
}

function GroupCard({ group, primary }: { group: AvailabilityGroup; primary: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canShowOnLayout = hasPermission("layouts:view");
  const where = group.location ? pathLabel(group.locationPath, group.location.name) : t("partSearch.noLocation", "Bez lokacije");

  /** "Prikaži na layoutu" (sprint 6 §5): the nearest placement of the mounted asset's position, or a toast when none. */
  async function showOnLayout(positionId: string) {
    try {
      const placements = await layoutsApi.placements(positionId);
      const p = placements[0];
      if (!p) { toast.info(t("layout.notPlaced", "Pozicija nije na layoutu.")); return; }
      navigate(`/layout/${p.layoutId}?marker=${p.distance === 0 ? positionId : p.placedPositionId}`);
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }
  return (
    <section className={`card avail-group${primary ? " is-primary" : ""}`}>
      <div className="card-h">
        <div style={{ minWidth: 0 }}>
          <h3 style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {primary && <span className="tag ok dot">{t("partSearch.availableNow", "Odmah dostupno")}</span>}
            <span>{where}</span>
            {group.location && <span className="muted mono" style={{ fontSize: 12 }}>{group.location.code}</span>}
          </h3>
          <div className="sub" style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
            <ConditionChip condition={group.condition} />
            <UsageChip usage={group.usage} />
            <span>{t("partSearch.groupCount", "{{count}} kom.", { count: group.count })}</span>
          </div>
        </div>
      </div>
      <div className="tbl-wrap">
        <table className="tbl list-card">
          <thead>
            <tr>
              <th>{t("assets.col.tag", "Oznaka")}</th>
              <th>{t("assets.field.serialNo", "Serijski broj")}</th>
              <th>{t("assets.col.position", "Logička pozicija")}</th>
              <th>{t("partSearch.col.info", "Napomena / povratak")}</th>
            </tr>
          </thead>
          <tbody>
            {group.assets.map((a) => {
              const pos = a.positionId ? shortPathLabel(a.positionPath, a.positionName) : null;
              return (
                <tr key={a.id}>
                  <td className="mono" data-label={t("assets.col.tag", "Oznaka")}><Link to={`/asseti/${a.id}`}><b>{a.tag}</b></Link></td>
                  <td className="mono" data-label={t("assets.field.serialNo", "Serijski broj")}>{a.serialNo ?? <span className="muted">—</span>}</td>
                  <td data-label={t("assets.col.position", "Logička pozicija")}>
                    {pos && a.positionId ? <PermLink permission="structure:view" to={`/struktura/${a.positionId}`} title={pos.full}>{pos.short}</PermLink> : <span className="muted">{usageLabel(t, a.usage)}</span>}
                    {" "}<ReplacementBadge asset={a} compact />
                    {a.positionId && canShowOnLayout && (
                      <button
                        type="button"
                        className="icon-btn"
                        style={{ display: "inline-grid", width: 28, height: 28, verticalAlign: "middle", marginLeft: 4, cursor: "pointer" }}
                        aria-label={t("layout.showOnLayout", "Prikaži na layoutu")}
                        title={t("layout.showOnLayout", "Prikaži na layoutu")}
                        onClick={() => { void showOnLayout(a.positionId!); }}
                      >
                        <Ic.layout />
                      </button>
                    )}
                  </td>
                  <td data-label={t("partSearch.col.info", "Napomena / povratak")}>
                    {a.expectedReturnAt
                      ? t("partSearch.returnAt", "Povratak sa servisa {{date}}", { date: formatDate(a.expectedReturnAt) })
                      : a.locationNote || <span className="muted">{a.condition !== "ISPRAVAN" ? conditionLabel(t, a.condition) : "—"}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
